<template>
  <main class="main-content">
    <component :is="NavItem"></component>
    <div class="admin-panel" >
      <div class="admin-header">
        <div class="icon-container blue">
          <img src="/img/icons/profile.png" alt="User Management Icon" class="white-icon"/>
        </div>
        <h2>Administrators</h2>
      </div>
      <div class="search-container">
        <input type="text" v-model="searchQuery" placeholder="Search by name, email, or role" class="search-input">
      </div>
      <div class="tab-navigation">
        <div
          v-for="tab in tabs"
          :key="tab"
          class="tab-item"
          :class="{ active: activeTab === tab }"
          @click="activeTab = tab"
        >
          {{ tab }}
        </div>
      </div>
      <div class="table-container">
        <div v-if="loading" class="loading-container">
          <p>Loading users...</p>
        </div>
        <div v-else-if="error" class="error-container">
          <p class="error-message">{{ error }}</p>
          <button @click="fetchUsers" class="retry-btn">Retry</button>
        </div>
        <table v-else class="user-table">
          <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(user, index) in filteredUsers" :key="user.id || index">
            <td>{{ user.firstName }} {{ user.lastName }}</td>
            <td>{{ user.email }}</td>
            <td>{{ user.role || 'Agent' }}</td>
            <td>
              <span class="status-badge" :class="getUserStatus(user).toLowerCase()">
                {{ getUserStatus(user) }}
              </span>
            </td>
            <td class="action-buttons">
              <div class="edit-dropdown" :class="{ 'dropdown-open': dropdownOpen === user.id }">
                <button class="btn-edit" @click="toggleDropdown(user.id)">
                  Edit
                  <i class="fas fa-chevron-down dropdown-icon"></i>
                </button>
                <div v-if="dropdownOpen === user.id" class="dropdown-menu">
                  <div class="dropdown-item" @click="openPasswordReset(user)">
                    <i class="fas fa-key"></i>
                    <span>Reset Password</span>
                  </div>
                </div>
              </div>
              <button class="btn-delete" @click="deleteUser(user)">Delete</button>
            </td>
          </tr>
          </tbody>
        </table>
        <div v-if="!loading && !error && users.length === 0" class="empty-state">
          <p>No users found</p>
        </div>
      </div>
      <div class="pagination">
        <div class="rows-per-page">
          <span>Rows per page:</span>
          <select v-model.number="rowsPerPage" @change="onRowsPerPageChange">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
        <div class="page-info">{{ paginationInfo }}</div>
        <div class="page-controls">
          <button class="first-page" @click="goToFirstPage" :disabled="currentPage === 1" title="First page">
            <span class="arrow-text">⇤</span>
          </button>
          <button class="prev-page" @click="previousPage" :disabled="currentPage === 1" title="Previous page">
            <span class="arrow-text">‹</span>
          </button>
          <span class="page-number">{{ currentPage }} of {{ totalPages }}</span>
          <button class="next-page" @click="nextPage" :disabled="currentPage === totalPages" title="Next page">
            <span class="arrow-text">›</span>
          </button>
          <button class="last-page" @click="goToLastPage" :disabled="currentPage === totalPages" title="Last page">
            <span class="arrow-text">⇥</span>
          </button>
        </div>
      </div>
      <div class="fab" @click="openModal">
        <img src="/img/icons/add.png" alt="Add new user"/>
      </div>
      <add-users-modal
        v-if="isModalVisible"
        @close="close"
        @captureUserDetails="forwardUserDetails"
      ></add-users-modal>

      <div v-if="passwordResetModal.visible" class="modal-overlay" @click="closePasswordReset">
        <div class="password-reset-modal" @click.stop>
          <div class="modal-header">
            <h3>Reset Password</h3>
            <button class="close-btn" @click="closePasswordReset">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="modal-body">
            <p class="user-info">Resetting password for: <strong>{{ passwordResetModal.user && passwordResetModal.user.firstName }} {{ passwordResetModal.user && passwordResetModal.user.lastName }}</strong></p>
            <p class="confirmation-text">Are you sure you want to initiate a password reset for this user?</p>
          </div>

          <div class="modal-footer">
            <button class="btn btn-cancel" @click="closePasswordReset" :disabled="passwordResetModal.loading">
              Cancel
            </button>
            <button
              class="btn btn-reset"
              @click="resetPassword"
              :disabled="passwordResetModal.loading"
            >
              <span v-if="passwordResetModal.loading">
                <i class="fas fa-spinner fa-spin"></i>
                Resetting...
              </span>
              <span v-else>Reset Password</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
/* eslint-disable */
import axios from 'axios';
import AddUsersModal from '@/admin/components/modals/AddUsersModal.vue';
import NavItem from '@/admin/components/nav/NavItem.vue';
import EditUser from '@/admin/components/EditUser.vue';

export default {
  name: 'UserManagement',
  computed: {
    NavItem() {
      return NavItem;
    },
    filteredUsers() {
      const isStaffTab = this.activeTab === 'Admin' || this.activeTab === 'APA User';

      // For Admin and APA User tabs, pagination is handled server-side
      if (isStaffTab) {
        // Apply search filter only
        let filtered = this.users;

        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          filtered = filtered.filter(user => {
            return (
              (user.firstName || '').toLowerCase().includes(query) ||
              (user.lastName || '').toLowerCase().includes(query) ||
              (user.fullName || '').toLowerCase().includes(query) ||
              (user.email || '').toLowerCase().includes(query) ||
              (user.role || '').toLowerCase().includes(query)
            );
          });
        }

        this.filteredUsersCount = this.totalRecords;
        return filtered;
      } else {
        // For other tabs, use client-side pagination
        let filtered = this.users;

        // Apply search filter
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          filtered = filtered.filter(user => {
            return (
              (user.firstName || '').toLowerCase().includes(query) ||
              (user.lastName || '').toLowerCase().includes(query) ||
              (user.fullName || '').toLowerCase().includes(query) ||
              (user.email || '').toLowerCase().includes(query) ||
              (user.role || '').toLowerCase().includes(query)
            );
          });
        }

        // Apply tab filter
        if (this.activeTab !== 'All') {
          filtered = filtered.filter(user => {
            if (this.activeTab === 'Agent') return user.role === 'Agent' || !user.role;
            if (this.activeTab === 'Bancassurance') return user.role === 'Bancassurance';
            if (this.activeTab === 'APA User') return user.role === 'APA User';
            if (this.activeTab === 'Admin') return user.role === 'Admin';
            return true;
          });
        }

        // Store filtered results for pagination calculation
        this.filteredUsersCount = filtered.length;

        // Apply pagination
        const start = (this.currentPage - 1) * parseInt(this.rowsPerPage);
        const end = start + parseInt(this.rowsPerPage);
        return filtered.slice(start, end);
      }
    },
    totalPages() {
      const isStaffTab = this.activeTab === 'Admin' || this.activeTab === 'APA User';

      if (isStaffTab) {
        // For server-side pagination, calculate based on totalRecords
        return Math.ceil(this.totalRecords / parseInt(this.rowsPerPage));
      } else {
        // For client-side pagination
        return Math.ceil(this.filteredUsersCount / parseInt(this.rowsPerPage));
      }
    },
    paginationInfo() {
      if (this.filteredUsersCount === 0) {
        return '0 of 0';
      }
      const pageSize = parseInt(this.rowsPerPage);
      const start = (this.currentPage - 1) * pageSize + 1;
      const end = Math.min(this.currentPage * pageSize, this.filteredUsersCount);
      return `${start}-${end} of ${this.filteredUsersCount}`;
    }
  },
  components: { AddUsersModal, NavItem, EditUser },
  data() {
    return {
      activeTab: 'All',
      tabs: ['All', 'Agent', 'Bancassurance', 'APA User', 'Admin'],
      users: [],
      rowsPerPage: 10,
      currentPage: 1,
      searchQuery: '',
      filteredUsersCount: 0,
      isModalVisible: false,
      loading: false,
      error: null,
      dropdownOpen: null,
      passwordResetModal: {
        visible: false,
        user: null,
        loading: false,
      },
      totalRecords: 0, // For server-side pagination
    };
  },
  async mounted() {
    await this.fetchUsers();
    document.addEventListener('click', this.closeDropdownOnClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeDropdownOnClickOutside);
  },
  methods: {
    /**
     * Fetches users based on the active tab
     * - Admin/APA User tabs: Fetches staff users with server-side pagination
     * - Other tabs: Fetches all agents with client-side pagination
     */
    async fetchUsers() {
      this.loading = true;
      this.error = null;

      try {
        const isStaffTab = this.activeTab === 'Admin' || this.activeTab === 'APA User';

        if (isStaffTab) {
          await this.fetchStaffUsers();
        } else {
          await this.fetchAgents();
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        this.handleFetchError(error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetches staff users (Admin/APA User) from the API
     */
    async fetchStaffUsers() {
      const response = await axios.get('/lmsagent/login/findUsersByType', {
        params: {
          userType: 'S',
          pageNo: this.currentPage,
          pageSize: this.rowsPerPage
        }
      });

      const apiResponse = response.data;
      const data = apiResponse.data || apiResponse;

      if (data.content && Array.isArray(data.content)) {
        this.users = data.content.map(user => this.mapStaffUser(user));
        this.totalRecords = data.totalElements || data.total || this.users.length;
      } else if (Array.isArray(data)) {
        this.users = data.map(user => this.mapStaffUser(user));
        this.totalRecords = this.users.length;
      } else {
        this.users = [];
        this.totalRecords = 0;
      }
    },

    /**
     * Fetches agent users from the API
     */
    async fetchAgents() {
      const response = await axios.get('/lmsagent/login/findAllAgents');

      if (Array.isArray(response.data)) {
        this.users = response.data.map(agent => this.mapAgent(agent));
        this.totalRecords = this.users.length;
      } else {
        this.users = [];
        this.totalRecords = 0;
      }
    },

    /**
     * Maps staff user data from API to component format
     */
    mapStaffUser(user) {
      const nameParts = (user.userFullName || '').split(' ');
      const firstName = nameParts[0] || 'N/A';
      const lastName = nameParts.slice(1).join(' ') || '';

      return {
        id: user.userId,
        userId: user.userId,
        agentCode: user.agentCode,
        firstName,
        lastName,
        fullName: user.userFullName || 'N/A',
        email: user.userEmail || user.username || 'N/A',
        username: user.username,
        telephone: user.userTelephone,
        role: this.activeTab,
        roles: user.roles || [],
        status: user.userActive ? 'Active' : 'Inactive',
        userActive: user.userActive,
        ...user
      };
    },

    /**
     * Maps agent data from API to component format
     */
    mapAgent(agent) {
      return {
        id: agent.agentCode,
        agentCode: agent.agentCode,
        firstName: agent.agentName || 'N/A',
        lastName: '',
        fullName: agent.agentName || 'N/A',
        email: agent.agentEmail || 'N/A',
        role: 'Agent',
        status: this.determineAgentStatus(agent),
        shortDesc: agent.agentShtDesc || '',
        passwordReset: agent.agentPasswordReset,
        passwordResetCode: agent.agentPasswordResetCode,
        passwordResetDate: agent.agentPasswordResetDate,
        ...agent
      };
    },

    /**
     * Handles errors from fetch operations
     */
    handleFetchError(error) {
      let errorMessage = 'Failed to load users. Please try again.';

      if (error.response?.data) {
        const apiError = error.response.data;
        if (typeof apiError === 'string') {
          errorMessage = `Error: ${apiError}`;
        } else if (apiError.message) {
          errorMessage = `Error: ${apiError.message}`;
        } else if (apiError.error) {
          errorMessage = `Error: ${apiError.error}`;
        }
      }

      this.error = errorMessage;
      this.users = [];
      this.totalRecords = 0;
    },
    determineUserStatus(user) {
      // Handle status for users from the new endpoint
      if (user.status) return user.status;
      if (user.agentCode === 0) return 'System';
      if (user.agentPasswordReset === 'Y' || user.passwordReset === 'Y') return 'Password Reset Required';
      if (user.agentPasswordReset === 'N' || user.passwordReset === 'N') return 'Active';
      if ((user.agentEmail === null || user.email === null) && user.agentCode !== 0) return 'Incomplete';
      return 'Active';
    },
    determineAgentStatus(agent) {
      if (agent.agentCode === 0) return 'System';
      if (agent.agentPasswordReset === 'Y') return 'Password Reset Required';
      if (agent.agentPasswordReset === 'N') return 'Active';
      if (agent.agentEmail === null && agent.agentCode !== 0) return 'Incomplete';
      return 'Active';
    },
    getUserStatus(user) {
      return user.status || 'Unknown';
    },
    openModal() {
      this.isModalVisible = true;
    },
    close() {
      this.isModalVisible = false;
    },

    toggleDropdown(userId) {
      this.dropdownOpen = this.dropdownOpen === userId ? null : userId;
    },

    openPasswordReset(user) {
      this.dropdownOpen = null;
      this.passwordResetModal = {
        visible: true,
        user: user,
        loading: false,
      };
    },

    closePasswordReset() {
      this.passwordResetModal = {
        visible: false,
        user: null,
        loading: false,
      };
    },

    async resetPassword() {
      if (!this.passwordResetModal.user) {
        return;
      }

      this.passwordResetModal.loading = true;

      try {
        const user = this.passwordResetModal.user;
        const isStaffUser = user.role === 'Admin' || user.role === 'APA User';

        let response;

        if (isStaffUser) {
          const username = user.username || user.userEmail || user.email;

          if (!username) {
            throw new Error('Username not found for this user');
          }

          response = await axios.post(
            `/lmsagent/login/initiateResetUserPassword?username=${encodeURIComponent(username)}`
          );
        } else {
          let username = user.username || user.email;
          if (!username || username === 'N/A') {
            username = user.agentCode || user.id;
          }

          if (!username) {
            throw new Error('Username/Email not found for this user');
          }

          response = await axios.post(
            `/lmsagent/login/initiateResetUserPassword?username=${encodeURIComponent(username)}`
          );
        }

        if (response.data && response.data.success) {
          this.showSuccess('Password reset initiated successfully!');
          this.closePasswordReset();
          await this.fetchUsers();
        } else {
          throw new Error(response.data?.msg || 'Password reset failed');
        }
      } catch (error) {
        console.error('Error resetting password:', error);
        this.handlePasswordResetError(error);
      } finally {
        this.passwordResetModal.loading = false;
      }
    },

    /**
     * Handles password reset errors
     */
    handlePasswordResetError(error) {
      let errorMessage = 'Failed to reset password. Please try again.';

      if (error.response?.data) {
        const apiError = error.response.data;
        if (typeof apiError === 'string') {
          errorMessage = apiError;
        } else if (apiError.msg) {
          errorMessage = apiError.msg;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      this.showError(errorMessage);
    },

    /**
     * Shows success message
     */
    showSuccess(message) {
      if (this.$toast && this.$toast.success) {
        this.$toast.success(message);
      } else {
        alert(message);
      }
    },

    /**
     * Shows error message
     */
    showError(message) {
      if (this.$toast && this.$toast.error) {
        this.$toast.error(message);
      } else {
        alert(message);
      }
    },

    closeDropdownOnClickOutside(event) {
      if (!event.target.closest('.edit-dropdown')) {
        this.dropdownOpen = null;
      }
    },
    forwardUserDetails(userType) {
      this.close();
      this.$emit('captureUserDetails', userType);
    },
    editUser(user) {
      this.$emit('change-component', 'EditUser', user);
    },

    async deleteUser(user) {
      if (!user || !user.id) {
        return;
      }

      const fullName = `${user.firstName} ${user.lastName}`.trim() || user.fullName || 'this user';
      if (!confirm(`Are you sure you want to delete ${fullName}?`)) {
        return;
      }

      try {

        const index = this.users.findIndex(u => u.id === user.id);
        if (index > -1) {
          this.users.splice(index, 1);
          this.showSuccess('User deleted successfully');
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        this.showError('Failed to delete user. Please try again.');
      }
    },
    /**
     * Checks if current tab requires server-side pagination
     */
    isServerSidePagination() {
      return this.activeTab === 'Admin' || this.activeTab === 'APA User';
    },

    async goToFirstPage() {
      this.currentPage = 1;
      if (this.isServerSidePagination()) {
        await this.fetchUsers();
      }
    },

    async goToLastPage() {
      this.currentPage = this.totalPages;
      if (this.isServerSidePagination()) {
        await this.fetchUsers();
      }
    },

    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        if (this.isServerSidePagination()) {
          await this.fetchUsers();
        }
      }
    },

    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        if (this.isServerSidePagination()) {
          await this.fetchUsers();
        }
      }
    },

    async onRowsPerPageChange() {
      this.currentPage = 1;
      if (this.isServerSidePagination()) {
        await this.fetchUsers();
      }
    },
    resetPagination() {
      this.currentPage = 1;
    }
  },
  watch: {
    searchQuery() {
      this.resetPagination();
    },
    async activeTab() {
      this.resetPagination();
      // Refetch users when tab changes
      await this.fetchUsers();
    }
  }
};
</script>

<style scoped>
.user-profile span {
  font-size: 0.9rem;
  color: #333;
}

.admin-panel {
  background-color: #fff;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: block;
  position: relative;
  max-width: 100%;
  height: auto;
  border-radius: 15px;
}

.admin-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.25rem;
}

.icon-container {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.blue {
  background-color: #0047AB;
}

.white-icon {
  color: white;
}

.admin-header h2 {
  font-size: 1.3rem;
  color: #333;
  font-weight: 500;
}

.search-container {
  margin-bottom: 1rem;
}

.search-input {
  width: 30%;
  padding: 0.6rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
}

.tab-navigation {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 1.5rem;
}

.tab-item {
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  color: #555;
  cursor: pointer;
  transition: color 0.2s;
}

.tab-item.active {
  color: #0056b3;
  border-bottom: 2px solid #0056b3;
  font-weight: 500;
}

.fab {
  position: absolute !important;
  bottom: -2rem !important;
  right: -2rem !important;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #ffc107;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.main-content {
  flex: 1;
  padding: 1.5rem 2rem;
  overflow-y: auto;
  background-color: #f0f2f5;
  left: 16%;
  margin-top: 4%;
  width: 100%;
}

.loading-container {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.error-container {
  text-align: center;
  padding: 2rem;
}


.retry-btn {
  background-color: #0056b3;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
}

.retry-btn:hover {
  background-color: #004085;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
}

.user-table {
  width: 100%;
  border-collapse: collapse;
}

.user-table th {
  background-color: #f9f9f9;
  color: #555;
  font-weight: 500;
  text-align: left;
  padding: 12px 16px;
  font-size: 14px;
  border-bottom: 1px solid #eee;
}

.user-table td {
  padding: 16px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  color: #333;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

.active {
  background-color: #e6f7e6;
  color: #1e7e34;
}

.inactive {
  background-color: #ffe6e6;
  color: #bd2130;
}

.unknown {
  background-color: #fff3cd;
  color: #856404;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-edit {
  color: #0047CC;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
}

.btn-delete {
  color: #c82333;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 20px;
  font-size: 14px;
  color: #666;
}

.rows-per-page {
  display: flex;
  align-items: center;
  margin-right: 24px;
}

.rows-per-page span {
  margin-right: 8px;
}

.rows-per-page select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
}

.page-info {
  margin-right: 24px;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-controls button {
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #555;
  transition: all 0.2s ease;
}

.page-controls button:hover:not(:disabled) {
  background-color: #f5f5f5;
  border-color: #0056b3;
  color: #0056b3;
}

.page-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: #ccc;
}

.page-controls button .arrow-text {
  font-size: 16px;
  font-weight: bold;
  color: inherit;
  line-height: 1;
}

.page-number {
  padding: 0 12px;
  font-size: 14px;
  color: #555;
  font-weight: 500;
  min-width: 80px;
  text-align: center;
}

/* Edit Dropdown Styles */
.edit-dropdown {
  position: relative;
  display: inline-block;
}

.edit-dropdown .btn-edit {
  display: flex;
  align-items: center;
  gap: 4px;
}

.dropdown-icon {
  font-size: 0.8rem;
  transition: transform 0.2s;
}

.dropdown-open .dropdown-icon {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #e1e5e9;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  z-index: 1000;
  margin-top: 4px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;
  transition: background-color 0.2s ease;
  gap: 0.5rem;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item:first-child {
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}

.dropdown-item:last-child {
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}

.dropdown-item i {
  width: 16px;
  color: #666;
}

.dropdown-item:hover i {
  color: #0047CC;
}

/* Password Reset Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.password-reset-modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: #e9ecef;
  color: #333;
}

.modal-body {
  padding: 1.5rem;
}

.user-info {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #0047CC;
}

.confirmation-text {
  font-size: 0.95rem;
  color: #555;
  line-height: 1.5;
}

.input-group {
  margin-bottom: 1rem;
}

.input-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #0047CC;
  font-size: 1rem;
  z-index: 1;
}

.input-with-icon input {
  width: 100%;
  padding: 0.8rem 1rem 0.8rem 2.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.3s;
  box-sizing: border-box;
}

.input-with-icon input:focus {
  border-color: #0047CC;
  box-shadow: 0 0 0 2px rgba(0, 71, 204, 0.1);
  outline: none;
}

.input-with-icon input:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.error-message {
  color: #721c24;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  background-color: #5a6268;
  color: white;
}

.btn-cancel:hover:not(:disabled) {
  background-color: #495057;
}

.btn-reset {
  background-color: #0047CC;
  color: white;
}

.btn-reset:hover:not(:disabled) {
  background-color: #003d99;
}

.fa-spin {
  animation: fa-spin 1s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
